/**
 * Simple synchronous hash (FNV-1a variant) for generating preview tokens.
 * NOT cryptographically secure — used only to make URLs unguessable by bots.
 * Works in browser, Node, and Cloudflare Workers without async crypto.
 */
const PREVIEW_SALT = 'hbt-preview-2026';
const PREVIEW_TOKEN_LENGTH = 12;

export function generatePreviewToken(homeId: number): string {
  const input = `${PREVIEW_SALT}:${homeId}`;
  let h = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193); // FNV prime
  }
  // Convert to unsigned 32-bit, then hex. Repeat with shifted seed for more chars.
  const h1 = (h >>> 0).toString(16).padStart(8, '0');
  let h2seed = h ^ 0xa5a5a5a5;
  for (let i = 0; i < input.length; i++) {
    h2seed ^= input.charCodeAt(i);
    h2seed = Math.imul(h2seed, 0x01000193);
  }
  const h2 = (h2seed >>> 0).toString(16).padStart(8, '0');
  return (h1 + h2).slice(0, PREVIEW_TOKEN_LENGTH);
}

export function isValidPreviewToken(
  homeId: number,
  token: string | undefined | null
): boolean {
  if (!token) return false;
  return token === generatePreviewToken(homeId);
}

export const PREVIEW_PARAM = 'preview';

export const getSHA256Hash = async (message: string) => {
  const text = message.toLowerCase().trim();
  // encode as UTF-8
  const msgBuffer = new TextEncoder().encode(text);

  // hash the message
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

  // convert ArrayBuffer to Array
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  // convert bytes to hex string
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
};
